// Cache específico para dados essenciais do PDV
// Produtos, Categorias, Formas de Pagamento, Mesas e Comandas são armazenados localmente
// para funcionamento offline

import { getCachedData, setCachedData, getCacheTimestamp } from './offline-storage';
import { logger } from './logger';

const CACHE_KEYS = {
  PRODUCTS: (restaurantId: string) => `pdv_products_${restaurantId}`,
  CATEGORIES: (restaurantId: string) => `pdv_categories_${restaurantId}`,
  PAYMENT_METHODS: (restaurantId: string) => `pdv_payment_methods_${restaurantId}`,
  RESTAURANT: (restaurantId: string) => `pdv_restaurant_${restaurantId}`,
  TABLES: (restaurantId: string) => `pdv_tables_${restaurantId}`,
  COMMANDS: (restaurantId: string) => `pdv_commands_${restaurantId}`,
};

// Cache duration: 72 hours (3 days) for PDV data
const CACHE_MAX_AGE = 72 * 60 * 60 * 1000;

// Products
export async function cacheProducts<T>(restaurantId: string, products: T[]): Promise<void> {
  try {
    await setCachedData(CACHE_KEYS.PRODUCTS(restaurantId), products);
    logger.info('PDV Cache', `Cached ${products.length} products`);
  } catch (error) {
    logger.error('[PDV Cache] Error caching products:', error);
  }
}

export async function getCachedProducts<T>(restaurantId: string): Promise<T[] | null> {
  try {
    return await getCachedData<T[]>(CACHE_KEYS.PRODUCTS(restaurantId), CACHE_MAX_AGE);
  } catch (error) {
    logger.error('[PDV Cache] Error getting cached products:', error);
    return null;
  }
}

// Categories
export async function cacheCategories<T>(restaurantId: string, categories: T[]): Promise<void> {
  try {
    await setCachedData(CACHE_KEYS.CATEGORIES(restaurantId), categories);
    logger.info('PDV Cache', `Cached ${categories.length} categories`);
  } catch (error) {
    logger.error('[PDV Cache] Error caching categories:', error);
  }
}

export async function getCachedCategories<T>(restaurantId: string): Promise<T[] | null> {
  try {
    return await getCachedData<T[]>(CACHE_KEYS.CATEGORIES(restaurantId), CACHE_MAX_AGE);
  } catch (error) {
    logger.error('[PDV Cache] Error getting cached categories:', error);
    return null;
  }
}

// Payment Methods
export async function cachePaymentMethods<T>(restaurantId: string, methods: T[]): Promise<void> {
  try {
    await setCachedData(CACHE_KEYS.PAYMENT_METHODS(restaurantId), methods);
    logger.info('PDV Cache', `Cached ${methods.length} payment methods`);
  } catch (error) {
    logger.error('[PDV Cache] Error caching payment methods:', error);
  }
}

export async function getCachedPaymentMethods<T>(restaurantId: string): Promise<T[] | null> {
  try {
    return await getCachedData<T[]>(CACHE_KEYS.PAYMENT_METHODS(restaurantId), CACHE_MAX_AGE);
  } catch (error) {
    logger.error('[PDV Cache] Error getting cached payment methods:', error);
    return null;
  }
}

// Restaurant info
export async function cacheRestaurant<T>(restaurantId: string, restaurant: T): Promise<void> {
  try {
    await setCachedData(CACHE_KEYS.RESTAURANT(restaurantId), restaurant);
    logger.info('PDV Cache', 'Cached restaurant info');
  } catch (error) {
    logger.error('[PDV Cache] Error caching restaurant:', error);
  }
}

export async function getCachedRestaurant<T>(restaurantId: string): Promise<T | null> {
  try {
    return await getCachedData<T>(CACHE_KEYS.RESTAURANT(restaurantId), CACHE_MAX_AGE);
  } catch (error) {
    logger.error('[PDV Cache] Error getting cached restaurant:', error);
    return null;
  }
}

// Tables
export async function cacheTables<T>(restaurantId: string, tables: T[]): Promise<void> {
  try {
    await setCachedData(CACHE_KEYS.TABLES(restaurantId), tables);
    logger.info('PDV Cache', `Cached ${tables.length} tables`);
  } catch (error) {
    logger.error('[PDV Cache] Error caching tables:', error);
  }
}

export async function getCachedTables<T>(restaurantId: string): Promise<T[] | null> {
  try {
    return await getCachedData<T[]>(CACHE_KEYS.TABLES(restaurantId), CACHE_MAX_AGE);
  } catch (error) {
    logger.error('[PDV Cache] Error getting cached tables:', error);
    return null;
  }
}

// Commands
export async function cacheCommands<T>(restaurantId: string, commands: T[]): Promise<void> {
  try {
    await setCachedData(CACHE_KEYS.COMMANDS(restaurantId), commands);
    logger.info('PDV Cache', `Cached ${commands.length} commands`);
  } catch (error) {
    logger.error('[PDV Cache] Error caching commands:', error);
  }
}

export async function getCachedCommands<T>(restaurantId: string): Promise<T[] | null> {
  try {
    return await getCachedData<T[]>(CACHE_KEYS.COMMANDS(restaurantId), CACHE_MAX_AGE);
  } catch (error) {
    logger.error('[PDV Cache] Error getting cached commands:', error);
    return null;
  }
}

// Cache expiration status
export interface CacheExpirationStatus {
  isExpiringSoon: boolean; // True if cache expires in less than 6 hours
  isExpired: boolean;
  hoursRemaining: number | null;
  oldestCacheTimestamp: number | null;
}

const CACHE_EXPIRING_SOON_THRESHOLD = 6 * 60 * 60 * 1000; // 6 hours

export async function getCacheExpirationStatus(restaurantId: string): Promise<CacheExpirationStatus> {
  try {
    const cacheKeys = [
      CACHE_KEYS.PRODUCTS(restaurantId),
      CACHE_KEYS.CATEGORIES(restaurantId),
      CACHE_KEYS.PAYMENT_METHODS(restaurantId),
      CACHE_KEYS.RESTAURANT(restaurantId),
      CACHE_KEYS.TABLES(restaurantId),
      CACHE_KEYS.COMMANDS(restaurantId),
    ];

    const timestamps = await Promise.all(cacheKeys.map(key => getCacheTimestamp(key)));
    const validTimestamps = timestamps.filter((t): t is number => t !== null);

    if (validTimestamps.length === 0) {
      return {
        isExpiringSoon: false,
        isExpired: true,
        hoursRemaining: null,
        oldestCacheTimestamp: null,
      };
    }

    const oldestTimestamp = Math.min(...validTimestamps);
    const now = Date.now();
    const cacheAge = now - oldestTimestamp;
    const timeRemaining = CACHE_MAX_AGE - cacheAge;
    const hoursRemaining = Math.max(0, Math.floor(timeRemaining / (60 * 60 * 1000)));

    return {
      isExpiringSoon: timeRemaining > 0 && timeRemaining <= CACHE_EXPIRING_SOON_THRESHOLD,
      isExpired: timeRemaining <= 0,
      hoursRemaining: timeRemaining > 0 ? hoursRemaining : 0,
      oldestCacheTimestamp: oldestTimestamp,
    };
  } catch (error) {
    logger.error('[PDV Cache] Error getting cache expiration status:', error);
    return {
      isExpiringSoon: false,
      isExpired: false,
      hoursRemaining: null,
      oldestCacheTimestamp: null,
    };
  }
}
